import React, { useContext, useEffect, useId, useState } from "react";
import inputBgMd from "@assets/images/backgrounds/inputBgMd.webp";
import useApiError from "@hooks/useApiError";
import usePlacesAutocomplete, { getDetails } from "use-places-autocomplete";
import useOnclickOutside from "react-cool-onclickoutside";
import { UserContext } from "@src/context/UserContext";
import { getValueOrDefault } from "@src/utils/ConditionHelper";
import {
  ADDRESS_VALIDATION_URL,
  countryNameToCode,
  parseAddressComponents,
} from "@src/utils/AddressHelper";
import axios from "axios";
import type { IAddAddressWithout } from "@src/types/AppTypes";

const AddAddressWithout: React.FC<IAddAddressWithout> = ({
  address,
  setAddress,
  errors,
  errorType,
  addresses,
  addressId,
  isShippingPrimary,
  isBillingPrimary,
}) => {
  const user = useContext(UserContext);
  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
  });
  const [isAddressValidating, setIsAddressValidating] =
    useState<boolean>(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isCountryUnsupportedByGoogle, setIsCountryUnsupportedByGoogle] =
    useState<boolean>(false);
  const id = useId();

  const setApiError = useApiError();

  useEffect(() => {
    if (address) {
      setValue(address.street_name, false);
    }
  }, [address, setValue]);

  const ref = useOnclickOutside(
    () => {
      clearSuggestions();
    },
    { ignoreClass: ["swal2-container"] }
  );

  useEffect(() => {
    if (user && addresses.length === 0) {
      setAddress((prev) => {
        if (prev) {
          return {
            ...prev,
            is_primary:
              addressId === "shipping" ? isShippingPrimary : isBillingPrimary,
          };
        }
        return prev;
      });
    }
  }, [
    addresses,
    user,
    setAddress,
    addressId,
    isShippingPrimary,
    isBillingPrimary,
  ]);

  const handleSelect = (val) => () => {
    setValue(val.description, false);
    clearSuggestions();
    getDetails({ placeId: val.place_id })
      .then((e) => {
        const parsedAddress = parseAddressComponents(e.address_components);

        setAddress({
          ...address,
          street_name: parsedAddress.street || val.description,
          country: parsedAddress.country,
          city: parsedAddress.city,
          state: parsedAddress.state,
          zip_code: parsedAddress.zipCode,
        });
        setValue(
          getValueOrDefault(parsedAddress.street, val.description),
          false
        );
      })
      .catch((e) => {
        setApiError(e);
      });
  };

  const renderSuggestions = () =>
    data.map((suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
      } = suggestion;

      return (
        <li
          className="py-2 first:pt-0 last:pb-0 hover:opacity-80"
          key={place_id}
          onClick={handleSelect(suggestion)}
          role="button"
          aria-label="Suggestions"
        >
          <strong>{main_text}</strong> <strong>{secondary_text}</strong>
        </li>
      );
    });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "zip_code") {
      setAddress({ ...address, [name]: value.replace(/[^a-zA-Z0-9]/g, "") });
      return;
    }
    setAddress({ ...address, [name]: value });
  };

  // for address validation
  useEffect(() => {
    const validateAddress = async () => {
      try {
        const res = await axios.post(ADDRESS_VALIDATION_URL, {
          address: {
            addressLines: [address?.street_name],
            locality: address?.city,
            administrativeArea: address?.state,
            postalCode: address?.zip_code,
            regionCode:
              countryNameToCode[address?.country.toLowerCase()] ||
              address?.country,
          },
        });
        if (res) {
          const unconfirmedFields: string[] = [];
          const {
            addressComponents,
          }: {
            addressComponents: {
              componentName: {
                text: string;
                languageCode: string;
              };
              componentType: string;
              confirmationLevel:
                | "CONFIRMED"
                | "UNCONFIRMED_BUT_PLAUSIBLE"
                | "UNCONFIRMED_AND_UNLIKELY";
            }[];
          } = res.data.result.address;
          addressComponents.forEach((i) => {
            if (i.confirmationLevel.includes("UNCONFIRMED")) {
              if (
                i.componentType === "sublocality_level_1" ||
                i.componentType === "route"
              ) {
                unconfirmedFields.push("Street address");
              } else if (i.componentType === "locality") {
                unconfirmedFields.push("City");
              } else if (i.componentType === "postal_code") {
                unconfirmedFields.push("Zipcode");
              } else if (i.componentType === "administrative_area_level_1") {
                unconfirmedFields.push("State");
              } else if (i.componentType === "country") {
                unconfirmedFields.push("Country");
              }
            }
          });
          if (unconfirmedFields.length) {
            setInvalidFields(unconfirmedFields);
          } else {
            setInvalidFields([]);
            setIsAddressValidating(false);
          }
          setIsCountryUnsupportedByGoogle(false);
        }
      } catch (e) {
        if (
          e.response.data.error.code === 400 &&
          e.response.data.error.message.includes("Invalid region code")
        ) {
          setInvalidFields([]);
          setIsCountryUnsupportedByGoogle(true);
          setIsAddressValidating(false);
        }
      } finally {
        setIsAddressValidating(false);
      }
    };

    let timeout: NodeJS.Timeout | null;
    if (
      address?.city &&
      address?.state &&
      address?.country &&
      address?.street_name &&
      address?.zip_code
    ) {
      setIsAddressValidating(true);
      timeout = setTimeout(() => {
        validateAddress();
      }, 400);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [
    address?.city,
    address?.state,
    address?.country,
    address?.street_name,
    address?.zip_code,
  ]);

  return (
    <>
      <div className="relative z-[1]">
        <div className="grid grid-cols-2 gap-7 max-sm:gap-2 max-sm:grid-cols-1 f-family-mochiy-pop">
          <div className="col-span-2 max-sm:col-auto" ref={ref}>
            <label
              className="text-secondary text-base mb-2 block"
              htmlFor={id + "street"}
            >
              Street Address *
            </label>
            <div className="relative">
              <input
                id="street"
                name="street_name"
                className="relative p-5 text-black w-full bg-transparent focus-visible:outline-none"
                value={address?.street_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setValue(e.target.value);
                  handleChange(e);
                }}
                maxLength={125}
                disabled={!ready}
                placeholder="Enter street address"
              />
              <img
                className="absolute top-0 left-0 size-full -z-[1]"
                src={inputBgMd}
                alt=""
              />
              {status === "OK" && (
                <ul className="p-3 bg-[#D854BB] rounded-xl border-black border-2 overflow-y-auto absolute top-16 z-10 w-full">
                  {renderSuggestions()}
                </ul>
              )}
            </div>
            <span className="text-red-500">
              {errors !== null &&
              errors.addressType === errorType &&
              errors.type === "streetNameError"
                ? errors.message
                : null}
            </span>
          </div>
          <div className="col-span-2 max-sm:col-auto">
            <label
              className="text-secondary text-base mb-2 block"
              htmlFor={id + "street_2"}
            >
              Street Address 2
            </label>
            <div className="relative">
              <input
                id="street_2"
                name="street_name2"
                maxLength={125}
                className="relative p-5 text-black w-full bg-transparent focus-visible:outline-none"
                value={address?.street_name2}
                onChange={(e) => {
                  handleChange(e);
                }}
                placeholder="Enter street address 2"
              />
              <img
                className="absolute top-0 left-0 size-full -z-[1]"
                src={inputBgMd}
                alt=""
              />
            </div>
          </div>
          <div className="relative">
            <label
              className="text-secondary text-base mb-2 block"
              htmlFor={id + "country"}
            >
              Country *
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Enter country"
                name="country"
                value={address?.country}
                onChange={(e) => {
                  handleChange(e);
                }}
                className="relative p-5 text-black w-full bg-transparent focus-visible:outline-none"
              />
              <img
                className="absolute top-0 left-0 size-full -z-[1]"
                src={inputBgMd}
                alt=""
              />
            </div>
            <span className="text-red-500">
              {errors !== null &&
              errors.addressType === errorType &&
              errors.type === "countryError"
                ? errors.message
                : null}
            </span>
          </div>
          <div className="relative">
            <label
              className="text-secondary text-base mb-2 block"
              htmlFor={id + "state"}
            >
              State *
            </label>
            <div className="relative">
              <input
                id={id + "state"}
                name="state"
                placeholder="Enter state"
                type="text"
                value={address?.state}
                onChange={(e) => {
                  handleChange(e);
                }}
                className="relative p-5 text-black w-full bg-transparent focus-visible:outline-none"
              />

              <img
                className="absolute top-0 left-0 size-full -z-[1]"
                src={inputBgMd}
                alt=""
              />
            </div>
            <span className="text-red-500">
              {errors !== null &&
              errors.addressType === errorType &&
              errors.type === "stateError"
                ? errors.message
                : null}
            </span>
          </div>
          <div>
            <label
              className="text-secondary text-base mb-2 block"
              htmlFor={id + "city"}
            >
              City *
            </label>
            <div className="relative">
              <input
                id={id + "city"}
                name="city"
                placeholder="Enter city"
                type="text"
                value={address?.city}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleChange(e);
                }}
                className="relative p-5 text-black w-full bg-transparent focus-visible:outline-none"
              />
              <img
                className="absolute top-0 left-0 size-full -z-[1]"
                src={inputBgMd}
                alt=""
              />
            </div>
            <span className="text-red-500">
              {errors !== null &&
              errors.addressType === errorType &&
              errors.type === "cityError"
                ? errors.message
                : null}
            </span>
          </div>
          <div>
            <label
              className="text-secondary text-base mb-2 block"
              htmlFor={id + "zip"}
            >
              Zip *
            </label>
            <div className="relative">
              <input
                id={id + "zip"}
                value={address?.zip_code}
                className="relative p-5 text-black w-full bg-transparent focus-visible:outline-none"
                type="text"
                placeholder="Enter zip"
                name="zip_code"
                onChange={(e) => {
                  handleChange(e);
                }}
              />
              <img
                className="absolute top-0 left-0 size-full -z-[1]"
                src={inputBgMd}
                alt=""
              />
            </div>
            <span className="text-red-500">
              {errors !== null &&
              errors.addressType === errorType &&
              errors.type === "zipCodeError"
                ? errors.message
                : null}
            </span>
          </div>
        </div>
      </div>
      {isAddressValidating && (
        <div className="flex gap-1 mt-2">
          Validating address <i className="ri-loop-right-line btn-loader"></i>
        </div>
      )}
      {!isAddressValidating && invalidFields.length !== 0 && (
        <p className="text-base text-yellow-300 mt-2">
          Note: We couldn't verify {invalidFields.join(", ")}. Please
          double-check for any typos or inaccuracies.
        </p>
      )}
      {!isAddressValidating && isCountryUnsupportedByGoogle && (
        <p className="text-base text-yellow-300 mt-2">
          Note: We couldn't verify this address. Please double-check for any
          typos or inaccuracies.
        </p>
      )}
    </>
  );
};

export default AddAddressWithout;
